import { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';

export interface UsePaginationQueryOptions {
  /** 쿼리 파라미터 키 (기본값: 'page') */
  queryKey?: string;
  /** URL 쿼리 파라미터와 동기화 여부 (기본값: true) */
  syncWithUrl?: boolean;
  /** 초기 커서 */
  initialCursor?: string | null;
  /** 초기 페이지 인덱스 */
  initialPageIndex?: number;
  /** 탭 컨텍스트의 params (탭별 독립적인 상태를 위해 사용) */
  tabParams?: Record<string, unknown>;
  /** 탭 params 업데이트 함수 */
  onTabParamsUpdate?: (params: Record<string, unknown>) => void;
}

export interface UsePaginationQueryReturn {
  /** 현재 커서 */
  cursor: string | null;
  /** 현재 페이지 인덱스 */
  pageIndex: number;
  /** 커서 설정 */
  setCursor: (cursor: string | null) => void;
  /** 페이지 인덱스 설정 */
  setPageIndex: (index: number) => void;
  /** 커서와 페이지 인덱스를 함께 설정 */
  setCursorAndIndex: (cursor: string | null, index: number) => void;
}

/**
 * 페이지네이션 쿼리 파라미터 관리 훅
 * URL 쿼리 파라미터와 동기화하여 커서와 페이지 인덱스를 관리합니다.
 */
export function usePaginationQuery({
  queryKey = 'page',
  syncWithUrl = true,
  initialCursor = null,
  initialPageIndex = 1,
  tabParams,
  onTabParamsUpdate,
}: UsePaginationQueryOptions = {}): UsePaginationQueryReturn {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // 탭 params가 있으면 탭 params를 우선 사용, 없으면 URL에서 읽기
  const useTabParams = !!tabParams && !!onTabParamsUpdate;
  const urlCursor = syncWithUrl && !useTabParams ? (searchParams?.get(queryKey) ?? null) : null;
  const urlPageIndex =
    syncWithUrl && !useTabParams ? (searchParams?.get(`${queryKey}Index`) ?? null) : null;

  // 탭 params에서 값 읽기
  const tabCursor = useTabParams
    ? ((tabParams[queryKey] as string | null | undefined) ?? null)
    : null;
  const tabPageIndex = useTabParams
    ? (tabParams[`${queryKey}Index`] as string | null | undefined)
    : null;
  const parsedTabPageIndex = tabPageIndex
    ? (() => {
        const parsed = typeof tabPageIndex === 'string' ? parseInt(tabPageIndex, 10) : tabPageIndex;
        return !isNaN(parsed) && parsed > 0 ? parsed : null;
      })()
    : null;

  // 초기값 결정: 탭 params 우선, 없으면 URL, 없으면 initial 값
  const initialCursorValue = useTabParams
    ? tabCursor !== null && tabCursor !== undefined
      ? tabCursor
      : initialCursor
    : urlCursor !== null && urlCursor !== undefined
      ? urlCursor
      : initialCursor;

  const initialPageIndexValue = useTabParams
    ? (parsedTabPageIndex ?? (tabCursor ? 2 : initialPageIndex))
    : (() => {
        if (urlPageIndex) {
          const parsed = parseInt(urlPageIndex, 10);
          if (!isNaN(parsed) && parsed > 0) {
            return parsed;
          }
        }
        return urlCursor ? 2 : initialPageIndex;
      })();

  const [cursor, setCursorState] = useState<string | null>(initialCursorValue);
  const [pageIndex, setPageIndexState] = useState<number>(initialPageIndexValue);

  /** URL 또는 탭 params 업데이트 */
  const updateUrl = useCallback(
    (newCursor: string | null, newPageIndex: number) => {
      if (useTabParams && onTabParamsUpdate) {
        // 탭 params 업데이트
        const updatedParams: Record<string, unknown> = { ...tabParams };

        if (newCursor) {
          updatedParams[queryKey] = newCursor;
        } else {
          delete updatedParams[queryKey];
        }

        if (newPageIndex > 1) {
          updatedParams[`${queryKey}Index`] = newPageIndex.toString();
        } else {
          delete updatedParams[`${queryKey}Index`];
        }

        onTabParamsUpdate(updatedParams);
        return;
      }

      if (!syncWithUrl) return;

      try {
        if (typeof window === 'undefined') return;

        // 기존 쿼리 파라미터를 보존하기 위해 useSearchParams를 사용
        const params = new URLSearchParams(searchParams?.toString() || '');

        // 커서 업데이트
        if (newCursor) {
          params.set(queryKey, newCursor);
        } else {
          params.delete(queryKey);
        }

        // 페이지 인덱스 업데이트
        if (newPageIndex > 1) {
          params.set(`${queryKey}Index`, newPageIndex.toString());
        } else {
          params.delete(`${queryKey}Index`);
        }

        const newUrl = `${pathname}?${params.toString()}`;
        router.replace(newUrl, { scroll: false });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to update URL (this is normal in Storybook):', error);
        }
      }
    },
    [
      syncWithUrl,
      pathname,
      queryKey,
      router,
      searchParams,
      useTabParams,
      tabParams,
      onTabParamsUpdate,
    ]
  );

  /** 커서 설정 */
  const setCursor = useCallback(
    (newCursor: string | null) => {
      setCursorState(newCursor);
      updateUrl(newCursor, pageIndex);
    },
    [pageIndex, updateUrl]
  );

  /** 페이지 인덱스 설정 */
  const setPageIndex = useCallback(
    (newIndex: number) => {
      setPageIndexState(newIndex);
      updateUrl(cursor, newIndex);
    },
    [cursor, updateUrl]
  );

  /** 커서와 페이지 인덱스를 함께 설정 */
  const setCursorAndIndex = useCallback(
    (newCursor: string | null, newIndex: number) => {
      setCursorState(newCursor);
      setPageIndexState(newIndex);
      updateUrl(newCursor, newIndex);
    },
    [updateUrl]
  );

  // URL 또는 탭 params에서 변경 감지
  useEffect(() => {
    if (useTabParams) {
      // 탭 params에서 변경 감지
      if (tabCursor !== cursor) {
        setCursorState(tabCursor);
      }

      if (parsedTabPageIndex !== null && parsedTabPageIndex !== pageIndex) {
        setPageIndexState(parsedTabPageIndex);
      } else if (parsedTabPageIndex === null && tabCursor === null && pageIndex !== 1) {
        // 탭 params에 pageIndex가 없고 cursor도 없으면 첫 페이지
        setPageIndexState(1);
        setCursorState(null);
      } else if (parsedTabPageIndex === null && tabCursor === null && cursor !== null) {
        // 탭 params에 둘 다 없는데 cursor가 null이 아니면 null로 설정
        setCursorState(null);
      }
      return;
    }

    if (!syncWithUrl) return;

    // 커서 변경 감지 (null도 포함)
    if (urlCursor !== cursor) {
      setCursorState(urlCursor);
    }

    // 페이지 인덱스 변경 감지
    if (urlPageIndex) {
      const parsed = parseInt(urlPageIndex, 10);
      if (!isNaN(parsed) && parsed > 0 && parsed !== pageIndex) {
        setPageIndexState(parsed);
      }
    } else if (urlPageIndex === null && urlCursor === null && pageIndex !== 1) {
      // URL에 pageIndex가 없고 cursor도 없으면 첫 페이지
      setPageIndexState(1);
      setCursorState(null);
    } else if (urlPageIndex === null && urlCursor === null && cursor !== null) {
      // URL에 둘 다 없는데 cursor가 null이 아니면 null로 설정
      setCursorState(null);
    }
  }, [
    syncWithUrl,
    urlCursor,
    urlPageIndex,
    cursor,
    pageIndex,
    useTabParams,
    tabCursor,
    parsedTabPageIndex,
  ]);

  return {
    cursor,
    pageIndex,
    setCursor,
    setPageIndex,
    setCursorAndIndex,
  };
}
